import { Component, ElementRef, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, find, map, reduce, sortBy } from 'lodash';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { unsubscribeComponent } from '../../../shared/helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import { ModalsService } from '../../../shared/modals/modals.service';
import { CsvImporterService } from '../../../shared/services/csv-importer/csv-importer-service';
import { DateHelpers } from '../../../shared/services/date-helpers/date-helpers.service';
import { UtilitiesService } from '../../../shared/services/utilities/utilities.service';
import { IDataLoad } from '../../../shared/typings/interfaces/data-load.interface';
import { ISchool } from '../../../shared/typings/interfaces/school.interface';

import { CreateDataLoad } from '../../../store';
import {
  IParseGradesCsvPayload,
  IParsedGradesUpload,
  TValidGradesUploadType,
  IParsedGradebookCSVRow,
  IStudentGradebookUploadRow,
} from './gradebook-upload.interface';
import { SKEDULA_CSV_COLUMNS } from './gradebook-upload.constant';
import { IStudent } from '../../../shared/typings/interfaces/student.interface';
import { ImSchool } from '../../../shared/services/im-models/im-school';

interface IUploadHistoryRow {
  uploadName: string;
  userName: string;
  date: string;
}

interface IHeadersMap {
  course?: number;
  title?: number;
  department?: number;
  teacher?: number;
  studentid?: number;
  lastname?: number;
  firstname?: number;
  gradelevel?: number;
  category?: number;
  categoryworth?: number;
  avg?: number;
  section?: number;
  markingperiod?: number;
  ofc?: number;
}

@unsubscribeComponent
@Component({
  selector: 'gradebook-upload',
  template: require('./gradebook-upload.component.html'),
  styles: [require('./gradebook-upload.component.scss')],
})
export class GradebookUploadComponent implements OnInit {
  @ViewChild('fileInput', { static: true }) fileInput: ElementRef;
  @ViewChild('scrollContainer', { static: true }) scrollContainer: ElementRef;
  @Input() dataLoads$: Observable<IDataLoad[]>;
  @Input() students$: Observable<IStudent[]>;
  @Input() currentSchool: ISchool;

  uploadsHistory: IUploadHistoryRow[];
  students: IStudent[];
  dataSubscription: Subscription;
  displayBoxShadow = false;
  gradesUploadType: TValidGradesUploadType;
  infoText = 'Upload a CSV file to import student information for coursework and grades.';
  requirementsLink = 'https://docs.google.com/document/d/1g3eX8Y0w9LcqBO00A36EvJt2AoCM1BdLbWofTnPLqRM/edit?usp=sharing';

  constructor(
    @Inject('$window') public $window: any,
    private dateHelpers: DateHelpers,
    private csvImporterService: CsvImporterService,
    private modalsService: ModalsService,
    public store: Store<any>,
    public utilitiesService: UtilitiesService,
    public imSchool: ImSchool,
  ) {
    //
  }

  ngOnInit() {
    // hardcoded until other types are added (JE)
    this.gradesUploadType = 'Skedula';
    this.uploadsHistory = this.formatHistory(this.uploadsHistory);

    this.dataSubscription = combineLatest(this.dataLoads$, this.students$)
      .pipe(
        tap(([dataLoads, students]) => {
          this.students = students;
          const sortedDataLoads = sortBy(dataLoads, dataLoad => dataLoad.createdAt).reverse();
          const filteredDataLoads = filter(sortedDataLoads, dataLoad => dataLoad.context === 'MP_GRADES');
          this.uploadsHistory = map(filteredDataLoads, (dataLoad: IDataLoad) => {
            const {
              createdBy: { firstName, lastName },
              createdAt,
              metaData: { term, mp },
            } = dataLoad;
            const date = this.dateHelpers.getFormattedMoment(createdAt, 'MMM DD, YYYY');
            const uploadName = `Data upload - Marking Period ${mp}`;
            return { uploadName, userName: `${firstName} ${lastName}`, date };
          });
        }),
      )
      .subscribe();
  }

  public openUpload(): void {
    this.fileInput.nativeElement.click();
  }

  public formatHistory(history): IUploadHistoryRow[] {
    return map(history, item => {
      const {
        name,
        uploadedBy: { first, last },
        createdAt,
      } = item;
      const fullName = `${first} ${last}`;
      const date = this.dateHelpers.getFormattedFromISO(createdAt, 'MMM DD, YYYY');
      return {
        uploadName: name,
        userName: fullName,
        date,
      };
    });
  }

  async handleFileInput(files: FileList) {
    const type = this.gradesUploadType;
    if (files.length) {
      const fileToUpload = files[0];
      const { size, name } = fileToUpload;
      // check file size
      const mbs = size / 1000000; // convert bytes to MB
      const doubleExtensionRegex = /\.[\0-\uFFFF]*\.csv$/;
      // check for double extensions
      const parsedName = name.replace('3.03', ''); // remove 3.03 from skedula files for validation
      const doubleExtension = parsedName.match(doubleExtensionRegex); // regex checks for string ending in .****.csv
      if (mbs > 5 || doubleExtension) {
        const errors = { format: [] };
        if (mbs > 5) errors.format = [...errors.format, `File size (${mbs}MB) exceeds limit of 5MB`];
        if (doubleExtension) errors.format = [...errors.format, `File name includes a double extension`];
        this._createDataLoad({ errors, rows: [] });
      } else {
        const text = await this.csvImporterService.readCsv(fileToUpload);
        const columns = this._getColsForUploadType(type);
        const headerCase = 'camel';
        const parsedJson = this.csvImporterService.csvToJson(text, columns, headerCase);
        const payload = {
          rows: parsedJson as IParsedGradebookCSVRow[],
          schoolId: this.currentSchool._id,
          text,
          type,
        };
        const parsedData = this.parseGradesCsv(payload);
        const {
          rows,
          errors: { format, fileSize, recordLimit },
        } = parsedData;
        const mp = this._findUploadMP(parsedData.rows); // parsedData.rows[0] ? parseInt(parsedData.rows[0].markingPeriod) : null;
        const hasFormatErrors = format && format.length;
        const hasFileSizeError = fileSize && fileSize.length;
        const hasRecordLimitError = recordLimit && recordLimit.length;

        // check for errors in upload, reject if errors exist
        if (hasFormatErrors || hasFileSizeError || hasRecordLimitError) {
          const { errors } = parsedData;
          this._createDataLoad({ errors, rows: [], mp });
        } else {
          // no errors exist, parse data
          // shape uploaded data to expected schema shape for GradebookUploads collection (JE)
          const { gradesPayload, rowErrors } = this._shapeRowsForUpload(rows, this.currentSchool._id);
          if (rowErrors) {
            this._createDataLoad({ errors: { rows: rowErrors }, rows: [], mp });
          } else {
            const data = {
              title: 'Gradebook upload',
              recordCount: gradesPayload.length,
              mp,
            };
            this.modalsService
              .openCsvConfirmModal(data)
              .afterClosed()
              .subscribe((confirmed: false | true) => {
                const payload = {
                  errors: [],
                  rows: gradesPayload,
                  mp,
                };
                if (confirmed && gradesPayload.length) this._createDataLoad(payload);
              });
          }
        }
      }
    }
    // allows same file to be selected again (JE)
    this.fileInput.nativeElement.value = null;
  }

  _getColsForUploadType(type: TValidGradesUploadType): { [key: string]: number } {
    if (type === 'Skedula') return SKEDULA_CSV_COLUMNS;
  }

  _findUploadMP(rows: IParsedGradebookCSVRow[]): number {
    const rowWithMP = find(rows, row => !!row.markingPeriod);
    return rowWithMP ? parseInt(rowWithMP.markingPeriod) : null;
  }

  openFreshDesk() {
    this.$window.FreshWidget.show();
  }

  parseGradesCsv(payload: IParseGradesCsvPayload): IParsedGradesUpload {
    const { rows, text, type } = payload;
    // check csv for minimum required format (JE)
    const headers = text
      .split('\n')[0]
      .split(',')
      .map(header =>
        header
          .trim()
          .toLowerCase()
          .replace(/"/g, ''),
      );
    const formatErrors = this._validateFormat(type, headers);

    const result: IParsedGradesUpload = {
      rows: [],
      errors: {
        format: formatErrors,
        recordLimit: [],
      },
    };
    if (formatErrors && formatErrors.length) return result;

    // TODO: Add more validation in post-MVP work (JE)
    if (rows.length > 50000)
      result.errors.recordLimit = [`CSV exceeds upload record limit of 50000 rows: ${rows.length} present in file`];
    result.rows = rows;
    return result;
  }

  _validateFormat(uploadType: TValidGradesUploadType, headers: string[]): string[] {
    const response = [];
    // TODO: break these into helper methods once other upload types exist (JE)
    if (uploadType === 'Skedula') {
      const headersMap = reduce(
        headers,
        (res, header) => {
          if (!res[header]) res[header] = 0;
          res[header] += 1;
          return res;
        },
        {} as IHeadersMap,
      );
      const {
        course,
        title,
        department,
        teacher,
        studentid,
        lastname,
        firstname,
        gradelevel,
        section,
        markingperiod,
        avg,
        ofc,
      } = headersMap;
      if (!course) response.push('Course ID column is missing');
      if (!title) response.push('Title column is missing');
      if (!department) response.push('Department column is missing');
      if (!teacher) response.push('Teacher column is missing');
      if (!studentid) response.push('StudentID column is missing');
      if (!lastname) response.push('LastName column is missing');
      if (!firstname) response.push('FirstName column is missing');
      if (!gradelevel) response.push('GradeLevel column is missing');
      if (!section) response.push('Section column is missing');
      if (!markingperiod) response.push('MarkingPeriod column is missing');
      if (!avg) response.push('AVG column is missing');
      if (!ofc) response.push('OFC column is missing');
      const duplicateHeaders = find(Object.keys(headersMap), header => {
        // only check for duplicate headers from our list of expected headers (JE)
        return headersMap[header] > 1;
      });
      if (duplicateHeaders) response.push('Duplicate headers');
    }
    return response;
  }

  /* istanbul ignore next */
  _shapeRowsForUpload(
    rows: IParsedGradebookCSVRow[],
    schoolId: string,
  ): { gradesPayload: IStudentGradebookUploadRow[]; rowErrors: string[] } {
    let rowErrors;
    const studentRowMap = reduce(
      rows,
      (res, row) => {
        const hasContent = reduce(
          Object.keys(row),
          (acc, key) => {
            const sanitizedValue = row[key] ? row[key].replace(/"/g, '') : '';
            acc += sanitizedValue;
            return acc;
          },
          '',
        );
        if (hasContent && hasContent.length) {
          const { studentId, avg, course, section, markingPeriod } = row;
          if (!studentId || !course || !section || !markingPeriod) {
            rowErrors = [
              'Values are missing for one or more required columns in the selected CSV file. Required columns include: Course, Section, MarkingPeriod, StudentID.',
            ];
          }
          const _id = `${studentId}${schoolId}`;
          if (!res[_id]) res[_id] = {};
          if (!res[_id][course]) res[_id][course] = { courseId: course, section: parseInt(section) };
          res[_id][course].avg = avg ? parseInt(avg) : null;
        }
        return res;
      },
      {},
    );
    const studentIds = Object.keys(studentRowMap);
    const gradesPayload = map(studentIds, studentId => {
      const coursesObj = studentRowMap[studentId];
      const courses = map(Object.keys(coursesObj), course => coursesObj[course]);
      const row = {
        studentId,
        courses,
      };
      return row;
    });
    return { gradesPayload, rowErrors };
  }

  /* istanbul ignore next */
  _createDataLoad(payload: { errors; rows: IStudentGradebookUploadRow[]; mp?: number }) {
    const { errors, rows, mp } = payload;
    const combinedErrors = reduce(
      Object.keys(errors),
      (res, key) => {
        const keyErrors = errors[key];
        res = [...res, ...keyErrors];
        return res;
      },
      [],
    );
    const dataLoadPayload = {
      schoolId: this.currentSchool._id,
      errors: combinedErrors,
      type: 'CSV_UPLOAD',
      context: 'MP_GRADES',
      toCreate: rows,
      meta: {
        term: this.imSchool.getCurrentTermYear(this.currentSchool),
        mp,
      },
      toUpdate: [],
    };
    // dispatch action to create data load
    this.store.dispatch(new CreateDataLoad(dataLoadPayload));
    // check if csv fixing modal can be opened
    const rejectionErrors = combinedErrors.length;
    if (rejectionErrors) {
      this.modalsService.openCsvUploadsErrorModal(payload.errors, 'MP_GRADES');
    }
  }

  onScroll(): void {
    const scrollTop = this.scrollContainer.nativeElement.scrollTop;
    this.displayBoxShadow = this.utilitiesService.showListPanelShadow(scrollTop);
  }
}
